import React, { Fragment } from "react";
import styled from "styled-components";
import I10n from '../features/i10n';
import MsxIcons from '../features/msx-icons';
import PropTypes from 'prop-types';
import ProductLink from "./product_link";
import { buildSoftwareLink } from "../utils/url-builder";
import InformationIcon from "../atoms/information-icon";
import DropdownMenu from "./dropdown_menu";
import ProductInformationCache from '../persistence/description-cache';
import { PRIMARY_COLOR } from "../color-constants";
import { LICENSE_MODELS } from "../constants";

const TableLicenceGroupTitle = styled.tr`
`;

const TableLicenceGroupTitleCell = styled.td`
  background-color: white;
  color: #000000;
  padding: 0px;
  border-top: 1px solid #D9D9D9;
  border-left: 1px solid #D9D9D9;
  border-right: 1px solid #D9D9D9;
  vertical-align: middle;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  ${props => props.hideRow ? `
    border-bottom: 1px solid #D9D9D9;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  ` : ``}
  overflow: hidden;
`;

const TableLicenceGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  margin-left: 10px;
  font-weight: 700;
  cursor: pointer;
`;

const TableHeaderCell = styled.td`
  background-color: #F3F3F3;
  color: #626469;
  font-weight: 400;
  font-size: 12px;
  padding: 0px;
  vertical-align: middle;

  :first-of-type {
    border-left: 1px solid #D9D9D9;
  }

  :last-of-type {
    border-right: 1px solid #D9D9D9;
  }

`;

const DescriptionTableHeaderCell = styled(TableHeaderCell)`
  @media (max-width: 450px) {
    display: none;
  }
`;

const NumberHeaderCell = styled(TableHeaderCell)`
  text-align: center;
`;

const TableCell = styled.td`
  padding: 0px;
  vertical-align: middle;
  background-color: white;

  :first-of-type {
    border-left: 1px solid #D9D9D9;
    ${props => props.fromLastRow ? `border-bottom-left-radius: 8px;` : ``}
  }

  :last-of-type {
    border-right: 1px solid #D9D9D9;
    ${props => props.fromLastRow ? `border-bottom-right-radius: 8px;` : ``}
  }

  ${props => props.fromLastRow ? `border-bottom: 1px solid #D9D9D9;` : ``}
`;

const CellContent = styled.div`
  padding: 10px;
  ${props => props.hideCell ? `padding: 0px 10px; height: 0px` : ``}
`;

const NumberCell = styled(TableCell)`
  font-size: 14px;
  text-align: center;
`;

const TableRow = styled.tr`
  ${props => props.hideRow ? 'visibility: hidden;' : 'border-bottom: 1px solid #D9D9D9;'}
`;

const TableHeaderRow = styled.tr`
  ${props => props.hideRow ? 'visibility: hidden;' : ''}
`;

const ArrowIconWrapper = styled.div`
  user-select: unset;
  transition: transform 0.3s;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: content-box;
  min-width: 32px;
  width: 32px;
  max-width: 32px;
  height: 32px;
`;

const Link = styled.a`
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  text-decoration: none;
  cursor: pointer;

  color: ${PRIMARY_COLOR};
  color: var(--se-secondary, ${PRIMARY_COLOR});

  :hover {
    text-decoration: underline;
  }
`;

const LinkLabel = styled.span`
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  color: #A0A4A8;
`;

const IndexLabel = styled.span``;

const DescriptionCellContent = styled(CellContent)`
  position: absolute;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
`;

const LicenceDescriptionLabel = styled.span`

  font-style: normal;
  color: #626469;
  font-size: 12px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;

`;

const DescriptionCell = styled(TableCell)`
  position: relative;
  width: 100%;
  @media (max-width: 450px) {
    display: none;
  }
`;

const ProductLinkWrapper = styled.div`
  display: flex;
  font-weight: 700;
  font-size: 12px;
  align-items: center;
`;

// Hidden cell with expect colspan in order to have some margin between rows
const MarginCell = styled.td`
  padding: 5px;
  vertical-align: middle;
`;

const InformationIconWrapper = styled.div`
  cursor: pointer;
  margin-left: 5px;
`;

const LinksWrapper = styled.div`
  padding: 10px;
  min-width: 200px;
`;

class BomLicenceGroup extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: true,
    }
  }

  static propTypes = {
    licenceGroup: PropTypes.object,
    startIdx: PropTypes.number,
    licenseModel: PropTypes.string
  }

  renderLinksToggle = () => {
    return (
    <InformationIconWrapper>
      <InformationIcon></InformationIcon>
    </InformationIconWrapper>
    );
  }

  renderLinksContent = (links) => {
    return (<LinksWrapper>
      {links.map((link, idx) => (
        <div key={idx}>
          <LinkLabel>
            {I10n.getOrDefault(`bom.licence.link.${link.labelKey}.label`, link.label)}
          </LinkLabel>
          <Link href={buildSoftwareLink(link.link)} target="_blank">
            {I10n.getOrDefault(`bom.licence.link.${link.softwareNameKey}.software`, link.softwareName)}
            <MsxIcons name="software-link" size="small"></MsxIcons>
          </Link>
        </div>
      ))}
    </LinksWrapper>);
  }

  renderLicenceRow = (softwareLicence, index, hideRow, isLastRow) => {
    const links = softwareLicence.links || [];
    return (
      <TableRow hideRow={hideRow}>
        <NumberCell fromLastRow={isLastRow}><CellContent hideCell={hideRow}><IndexLabel>{index}</IndexLabel></CellContent></NumberCell>
        <TableCell fromLastRow={isLastRow}>
          <CellContent hideCell={hideRow}>
            <ProductLinkWrapper>
              <ProductLink reference={softwareLicence.id} disabled={LICENSE_MODELS.SUBSCRIPTION === this.props.licenseModel}></ProductLink>
              {links.length > 0 && <DropdownMenu data-clickable
                toggleElement={this.renderLinksToggle()}
                containerElement={this.renderLinksContent(links)}
                allowTop={true}></DropdownMenu>
              }
            </ProductLinkWrapper>
          </CellContent>
        </TableCell>
        <DescriptionCell fromLastRow={isLastRow} title={ProductInformationCache.getDescription(softwareLicence.id) || softwareLicence.description}>
          <DescriptionCellContent hideCell={hideRow}>
            <LicenceDescriptionLabel>
              {ProductInformationCache.getDescription(softwareLicence.id) || softwareLicence.description}
            </LicenceDescriptionLabel>
          </DescriptionCellContent>
        </DescriptionCell>
        <TableCell fromLastRow={isLastRow}><CellContent hideCell={hideRow}>{softwareLicence.quantity}</CellContent></TableCell>
        <TableCell fromLastRow={isLastRow}>
          <CellContent hideCell={hideRow}></CellContent>
        </TableCell>
      </TableRow>
    );
  };

  render = () => {
    const hideRow = !this.state.isOpen;
    return (
      <Fragment>
       <TableLicenceGroupTitle onClick={() => { this.setState({isOpen: !this.state.isOpen}); }}>
        <TableLicenceGroupTitleCell hideRow={hideRow} colSpan={5}>
        <TableLicenceGroupHeader>
          {this.props.licenceGroup.name}
          <ArrowIconWrapper isOpen={this.state.isOpen}>
            <MsxIcons name="down-arrow" size="nano"></MsxIcons>
          </ArrowIconWrapper>
        </TableLicenceGroupHeader>
       </TableLicenceGroupTitleCell>
      </TableLicenceGroupTitle>
          <>
            <TableHeaderRow hideRow={hideRow}>
              <NumberHeaderCell><CellContent hideCell={hideRow}>{I10n.get('bom.order')}</CellContent></NumberHeaderCell>
              <TableHeaderCell><CellContent hideCell={hideRow}>{I10n.get('bom.reference')}</CellContent></TableHeaderCell>
              <DescriptionTableHeaderCell><CellContent hideCell={hideRow}>{I10n.get('bom.description')}</CellContent></DescriptionTableHeaderCell>
              <TableHeaderCell><CellContent hideCell={hideRow}>{I10n.get('bom.quantity')}</CellContent></TableHeaderCell>
              <TableHeaderCell><CellContent hideCell={hideRow}></CellContent></TableHeaderCell>
            </TableHeaderRow>
            {this.props.licenceGroup.licences.map((licence, idx)  => (<Fragment key={idx}>{this.renderLicenceRow(licence, this.props.startIdx + idx, hideRow, this.props.licenceGroup.licences.length === (idx + 1))}</Fragment>))}
            {true && <TableRow><MarginCell colSpan={5}></MarginCell></TableRow>}
          </>
      </Fragment>
    );
  }
}

export default BomLicenceGroup;
